import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import { STRINGS } from '@/constants/strings';
import {
  GuardianCircleMemberStatus,
  type GuardianCircleData,
  type GuardianCircleMember,
} from '@/features/guardian-player/types';
import type { PlayerGuardian } from '@/services/get-player-guardians';

import { formatGuardianApiDate } from './date';

const strings = STRINGS.guardianPlayer.guardianCircle;

/** API status/role → the local member status used to drive row styling and routing. */
function mapMemberStatus(member: PlayerGuardian): GuardianCircleMemberStatus {
  if (member.isOwner || member.role?.toLowerCase() === 'owner') {
    return GuardianCircleMemberStatus.Creator;
  }
  switch ((member.status ?? '').toLowerCase()) {
    case 'expired':
      return GuardianCircleMemberStatus.Expired;
    case 'declined':
    case 'rejected':
      return GuardianCircleMemberStatus.Declined;
    case 'pending':
    case 'waiting':
    case 'invited':
      return GuardianCircleMemberStatus.Waiting;
    default:
      return GuardianCircleMemberStatus.Joined;
  }
}

function relationshipLabel(relationship?: string): string {
  const key = (relationship ?? '').toLowerCase();
  const known = (strings.relationships as Record<string, string>)[key];
  if (known) return known;
  if (!relationship) return strings.relationships.other;
  return relationship.charAt(0).toUpperCase() + relationship.slice(1);
}

/** Meta line trailer: "· since Sep 2023" for active members, "· Invited 2 days ago" for invites. */
function buildMetaLabel(
  status: GuardianCircleMemberStatus,
  member: PlayerGuardian,
): string | undefined {
  if (status === GuardianCircleMemberStatus.Creator) {
    const monthYear = formatGuardianApiDate(member.since, 'MMM yyyy');
    return monthYear ? strings.metaCreated.replace('{date}', monthYear) : undefined;
  }
  if (
    status === GuardianCircleMemberStatus.Joined ||
    status === GuardianCircleMemberStatus.Active
  ) {
    const monthYear = formatGuardianApiDate(member.since, 'MMM yyyy');
    return monthYear ? strings.metaSince.replace('{date}', monthYear) : undefined;
  }
  const invitedDate = member.invitedAt ? parseISO(member.invitedAt) : null;
  if (!invitedDate || !isValid(invitedDate)) return undefined;

  const days = differenceInCalendarDays(new Date(), invitedDate);
  let timeString = '';
  if (days === 0) {
    timeString = 'today';
  } else if (days === 1) {
    timeString = '1 day ago';
  } else {
    timeString = `${days} days ago`;
  }

  return strings.metaInvited.replace('{time}', timeString);
}

/**
 * Maps the co-guardian API payload into the view model the Guardian Circle screen
 * renders. `activeAdultsCount` counts the adults who can currently see the player
 * (creator + joined); `isPrimaryGuardian` is true when the signed-in guardian owns
 * the profile (drives Invite vs Leave, and the empty state).
 */
export function buildGuardianCircle(response: PlayerGuardian[] | undefined): GuardianCircleData {
  const members: GuardianCircleMember[] = (response ?? []).map(member => {
    const status = mapMemberStatus(member);
    return {
      ...member,
      avatarUrl:
        member.photoUrl && member.photoUrl.startsWith('http') ? member.photoUrl : undefined,
      status,
      leadLabel: relationshipLabel(member.relationship),
      metaLabel: buildMetaLabel(status, member),
    };
  });

  // Sort so the current user (isSelf) is always at the top
  members.sort((a, b) => {
    if (a.isSelf && !b.isSelf) return -1;
    if (!a.isSelf && b.isSelf) return 1;
    return 0;
  });

  const activeAdultsCount = members.filter(
    member =>
      member.status === GuardianCircleMemberStatus.Creator ||
      member.status === GuardianCircleMemberStatus.Joined,
  ).length;

  const isPrimaryGuardian = (response ?? []).some(member => member.isSelf && member.isOwner);

  return { members, activeAdultsCount, isPrimaryGuardian };
}
